import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Select';
import { cn } from '../../../lib/utils';
import styles from './index.module.scss';

interface BirthDatePickerProps {
  value?: Date | string;
  onChange: (date: string | undefined) => void;
  className?: string;
  label?: string;
  error?: string;
  config?: any;
  onBlur?: () => void;
}

const currentYear = new Date().getFullYear();

const BirthDatePicker: React.FC<BirthDatePickerProps> = ({
  value,
  onChange,
  className,
  error,
  config,
  onBlur
}) => {
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');

  // Initialize from value
  useEffect(() => {
     if (value instanceof Date && !isNaN(value.getTime())) {
    setDay(value.getDate().toString());
    setMonth((value.getMonth() + 1).toString());
    setYear(value.getFullYear().toString());
  } else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Handle "YYYY-MM-DD" string
    const [yyyy, mm, dd] = value.split("-");
    setYear(yyyy);
    setMonth(parseInt(mm, 10).toString());
    setDay(parseInt(dd, 10).toString());
  }
  }, [value]);

  // Helper to add/subtract units from a date
  const addToDate = (date: Date, unit: string, value: number) => {
    const newDate = new Date(date);
    switch (unit) {
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + value);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + value);
        break;
      case 'days':
      case 'day':
        newDate.setDate(newDate.getDate() + value);
        break;
      default:
        break;
    }
    return newDate;
  };

  // Calculate minDate and maxDate based on config
  let minDate: Date | null = null;
  let maxDate: Date | null = null;
  const today = new Date();

  if (config && config.pastValidation && config.pastValidation.enabled) {
    const { unit, minValue, maxValue } = config.pastValidation;
    // minDate: today - maxValue, maxDate: today - minValue
    minDate = addToDate(today, unit, -maxValue);
    maxDate = addToDate(today, unit, -minValue);
  }
  if (config && config.futureValidation && config.futureValidation.enabled) {
    const { unit, minValue, maxValue } = config.futureValidation;
    // minDate: today + minValue, maxDate: today + maxValue
    const futureMin = addToDate(today, unit, minValue);
    const futureMax = addToDate(today, unit, maxValue);
    // If pastValidation is also enabled, take intersection
    if (minDate && maxDate) {
      if (futureMin > maxDate || futureMax < minDate) {
        // No intersection, set impossible range
        minDate = maxDate = null;
      } else {
        minDate = futureMin > minDate ? futureMin : minDate;
        maxDate = futureMax < maxDate ? futureMax : maxDate;
      }
    } else {
      minDate = futureMin;
      maxDate = futureMax;
    }
  }
  if (!minDate) minDate = new Date(new Date().getFullYear() - 100, 0, 1);
  if (!maxDate) maxDate = today;

  // Generate years based on minDate and maxDate
  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i).filter(
    (y) => y >= minYear && y <= maxYear
  );

  // Generate months based on selected year, minDate, and maxDate
  let monthsList = months;
  if (year) {
    const selectedYear = parseInt(year);
    let startMonth = 1;
    let endMonth = 12;
    if (selectedYear === minYear) startMonth = minDate.getMonth() + 1;
    if (selectedYear === maxYear) endMonth = maxDate.getMonth() + 1;
    monthsList = months.filter(
      (m: { value: string; label: string }) => parseInt(m.value) >= startMonth && parseInt(m.value) <= endMonth
    );
  }

  // Generate days based on selected year, month, minDate, and maxDate
  let daysList = Array.from({ length: 31 }, (_, i) => i + 1);
  if (year && month) {
    const selectedYear = parseInt(year);
    const selectedMonth = parseInt(month);
    let startDay = 1;
    let endDay = getDaysInMonth(selectedMonth, selectedYear);
    if (selectedYear === minYear && selectedMonth === minDate.getMonth() + 1) startDay = minDate.getDate();
    if (selectedYear === maxYear && selectedMonth === maxDate.getMonth() + 1) endDay = maxDate.getDate();
    daysList = Array.from({ length: endDay - startDay + 1 }, (_, i) => startDay + i);
  }

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

  // Validate age and update parent
  const updateDate = (newDay: string, newMonth: string, newYear: string) => {
    if (newDay && newMonth && newYear) {
      const date = new Date(parseInt(newYear), parseInt(newMonth) - 1, parseInt(newDay));
      // Check if date is valid
      if (isNaN(date.getTime())) {
        onChange(undefined);
        return;
      }
      // Calculate age
      const today = new Date();
      const age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      const dayDiff = today.getDate() - date.getDate();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
      const formattedDate = formatDate(date);
      // if (actualAge >= 12) {
        onChange(formattedDate);
      // } else {
        // onChange(undefined);
      // }
    } else {
      onChange(undefined);
    }
  };

  const handleDayChange = (newDay: string) => {
    setDay(newDay);
    updateDate(newDay, month, year);
  };

  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth);
    // Reset day if it's invalid for the new month
    const maxDays = getDaysInMonth(parseInt(newMonth), parseInt(year) || currentYear);
    const currentDay = parseInt(day);
    if (currentDay > maxDays) {
      setDay('');
      updateDate('', newMonth, year);
    } else {
      updateDate(day, newMonth, year);
    }
  };

  const handleYearChange = (newYear: string) => {
    setYear(newYear);
    // Check if the current month is still valid for the new year
    let validMonth = month;
    let validDay = day;
    const selectedYear = parseInt(newYear);
    let startMonth = 1;
    let endMonth = 12;
    if (selectedYear === minDate.getFullYear()) startMonth = minDate.getMonth() + 1;
    if (selectedYear === maxDate.getFullYear()) endMonth = maxDate.getMonth() + 1;
    if (month) {
      const monthNum = parseInt(month);
      if (monthNum < startMonth || monthNum > endMonth) {
        validMonth = '';
        validDay = '';
        setMonth('');
        setDay('');
      }
    }
    // If month is valid, check if day is valid for the new year/month
    if (validMonth && day) {
      const maxDays = getDaysInMonth(parseInt(validMonth), selectedYear);
      const currentDay = parseInt(day);
      if (currentDay > maxDays) {
        validDay = '';
        setDay('');
      }
    }
    updateDate(validDay, validMonth, newYear);
  };

  // Check if current selection would result in underage
  // const isUnderage = () => {
  //   if (!day || !month || !year) return false;
    
  //   const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  //   if (isNaN(date.getTime())) return false;

  //   const today = new Date();
  //   const age = today.getFullYear() - date.getFullYear();
  //   const monthDiff = today.getMonth() - date.getMonth();
  //   const dayDiff = today.getDate() - date.getDate();
    
  //   const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
  //   return actualAge < 12;
  // };

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.dateGrid}>
        <Select value={day} onValueChange={handleDayChange} >
          <SelectTrigger className={styles.selectTrigger} onBlur={onBlur}>
            <SelectValue placeholder="Day" className={styles.selectValue} />
          </SelectTrigger>
          <SelectContent className={styles.selectContent}>
            {daysList.map((d: number) => (
              <SelectItem key={d} value={d.toString()} className={styles.selectItem}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={month} onValueChange={handleMonthChange}>
          <SelectTrigger className={styles.selectTrigger} onBlur={onBlur}>
            <SelectValue placeholder="Month" className={styles.selectValue} />
          </SelectTrigger>
          <SelectContent className={styles.selectContent}>
            {monthsList.map((m: { value: string; label: string }) => (
              <SelectItem key={m.value} value={m.value} className={styles.selectItem}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={year} onValueChange={handleYearChange}>
          <SelectTrigger className={styles.selectTrigger} onBlur={onBlur}>
            <SelectValue placeholder="Year" className={styles.selectValue} />
          </SelectTrigger>
          <SelectContent className={styles.selectContent}>
            {years.map((y) => (
              <SelectItem key={y} value={y.toString()} className={styles.selectItem}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(error) && (
        <p className={styles.errorMessage}>
          {error || "You must be at least 12 years old to register"}
        </p>
      )}
    </div>
  );
};

export default BirthDatePicker;

// Generate months
const months = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

// Generate days based on selected month and year
const getDaysInMonth = (month: number, year: number) => {
  return new Date(year, month, 0).getDate();
};